import { PartnerUserPermissions } from "providers/Auth";
import {
  ApplicantStatus,
  ApplicantType,
  ApplicationFeeStatus,
  ApplicationStatus,
  Applicant as SharedApplicant,
  Application as SharedApplication
} from "shared/types";
import {
  Applicant,
  Application,
  MakeDecisionRequirements
} from "types/application";

type AnyApplicant = Applicant | SharedApplicant;
type AnyApplication = Application | SharedApplication;

export interface ActionState {
  disabled: boolean;
  tooltip: string | undefined;
  hidden: boolean;
}

// ---------------------------------------------------------------------------
// ActionState factories
// ---------------------------------------------------------------------------

const hidden = (tooltip?: string): ActionState => ({
  disabled: true,
  hidden: true,
  tooltip
});

const disabled = (tooltip: string): ActionState => ({
  disabled: true,
  hidden: false,
  tooltip
});

const enabled: ActionState = {
  disabled: false,
  hidden: false,
  tooltip: undefined
};

// ---------------------------------------------------------------------------
// Common status groups
// ---------------------------------------------------------------------------

const DECIDED_STATUSES: ApplicationStatus[] = [
  ApplicationStatus.Approved,
  ApplicationStatus.Rejected,
  ApplicationStatus.ConditionalApproved
];

const FINAL_STATUSES: ApplicationStatus[] = [
  ...DECIDED_STATUSES,
  ApplicationStatus.Canceled
];

const isDecided = (status: ApplicationStatus) =>
  DECIDED_STATUSES.includes(status);

const isFinalized = (status: ApplicationStatus) =>
  FINAL_STATUSES.includes(status);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getNonCoSigners = (applicants: Pick<SharedApplicant, "role">[]) =>
  applicants.filter((a) => a.role !== ApplicantType.CoSigner).length;

const getNonCoSignersForApplication = (application: AnyApplication) =>
  getNonCoSigners([
    ...application.applicants,
    ...(application.pending_magic_links || [])
  ]);

export { getNonCoSigners };

// ---------------------------------------------------------------------------
// Contract — defines every application action getter
// ---------------------------------------------------------------------------

abstract class BaseApplicationActions {
  abstract get cancel(): ActionState;
  abstract get uncancel(): ActionState;
  abstract get refund(): ActionState;
  abstract get move(): ActionState;
  abstract get split(): ActionState;
  abstract get placeOnHold(): ActionState;
  abstract get resume(): ActionState;
  abstract get inviteApplicant(): ActionState;
  abstract get undoDecision(): ActionState;
  abstract get makeDecision(): ActionState;
}

// ---------------------------------------------------------------------------
// Constructor options
// ---------------------------------------------------------------------------

interface ApplicationActionsOptions {
  status: ApplicationStatus;
  permissions: PartnerUserPermissions;
  applicants?: AnyApplicant[];
  application?: AnyApplication;
  unitLabel?: string;
  selectedForMerge?: boolean;
  decisionAt?: string | null;
  requirements?: MakeDecisionRequirements[];
  undoDecisionDays?: number | null;
}

// ---------------------------------------------------------------------------
// ApplicationActions
// ---------------------------------------------------------------------------

export class ApplicationActions implements BaseApplicationActions {
  private status: ApplicationStatus;
  private permissions: PartnerUserPermissions;
  private applicants: AnyApplicant[];
  private application?: AnyApplication;
  private unitLabel: string;
  private selectedForMerge: boolean;
  private decisionAt?: string | null;
  private requirements: MakeDecisionRequirements[];
  private undoDecisionDays: number | null;

  constructor(opts: ApplicationActionsOptions) {
    this.status = opts.status;
    this.permissions = opts.permissions;
    this.applicants = opts.applicants ?? [];
    this.application = opts.application;
    this.unitLabel = opts.unitLabel ?? "unit";
    this.selectedForMerge = opts.selectedForMerge ?? false;
    this.decisionAt = opts.decisionAt;
    this.requirements = opts.requirements ?? [];
    this.undoDecisionDays =
      opts.undoDecisionDays !== undefined ? opts.undoDecisionDays : 30;
  }

  private get canCancel() {
    return this.permissions.canCancelApplications;
  }

  get cancel(): ActionState {
    if (this.status === ApplicationStatus.Canceled) return hidden();

    if (!this.canCancel)
      return hidden("You don't have permissions to cancel this application");

    if (isDecided(this.status))
      return disabled(
        "You can't cancel application once the decisions have been made"
      );

    return enabled;
  }

  get uncancel(): ActionState {
    if (this.status !== ApplicationStatus.Canceled) return hidden();

    if (!this.canCancel) return hidden();

    return enabled;
  }

  get refund(): ActionState {
    if (this.status === ApplicationStatus.Canceled) return hidden();

    if (!this.canCancel)
      return hidden("You don't have permissions to refund this application");

    if (isDecided(this.status))
      return disabled(
        "You can't refund application once the decisions have been made"
      );

    const list = this.applicants;

    const hasRefundable = list.some(
      (a) =>
        a.application_fee_status === ApplicationFeeStatus.Paid &&
        a.application_status === ApplicantStatus.Submitted
    );

    if (!hasRefundable) {
      const allRefunded = list.every(
        (a) => a.application_fee_status === ApplicationFeeStatus.Refunded
      );
      const hasSubmitted = list.some(
        (a) => a.application_status === ApplicantStatus.Submitted
      );

      let tooltip: string;
      if (hasSubmitted && allRefunded)
        tooltip = "Refund has already been requested for this application";
      else if (hasSubmitted)
        tooltip =
          "You can request a refund if someone has paid an application fee";
      else tooltip = "You can request a refund for submitted applications";

      return disabled(tooltip);
    }

    return enabled;
  }

  get move(): ActionState {
    const hasPermission = this.permissions.canChangeUnit;

    if (!hasPermission) return hidden();

    if (isFinalized(this.status))
      return disabled(
        `You can't move application to another ${this.unitLabel} once the decision have been made`
      );

    return enabled;
  }

  get split(): ActionState {
    if (!this.application) return hidden();

    if (this.status === ApplicationStatus.OnHold)
      return disabled("You need to resume the application before splitting");

    if (isDecided(this.status))
      return disabled(
        "You can't split the application once a decision has been made"
      );

    if (getNonCoSignersForApplication(this.application) < 2)
      return disabled(
        "Only applications with more than one applicant and/or occupant can be split"
      );

    return enabled;
  }

  get placeOnHold(): ActionState {
    if (isFinalized(this.status) || this.status === ApplicationStatus.OnHold)
      return hidden();

    if (this.selectedForMerge)
      return disabled("Cannot place on hold — application selected for merge");

    if (this.status !== ApplicationStatus.Submitted)
      return disabled("Only submitted applications can be put on hold");

    return enabled;
  }

  get resume(): ActionState {
    if (this.status !== ApplicationStatus.OnHold) return hidden();

    return enabled;
  }

  get inviteApplicant(): ActionState {
    if ([...FINAL_STATUSES, ApplicationStatus.Merged].includes(this.status))
      return disabled(
        "You can't invite applicants once the decisions have been made"
      );

    if (this.status === ApplicationStatus.OnHold)
      return disabled("To invite applicants, resume application first");

    return enabled;
  }

  get undoDecision(): ActionState {
    if (!this.permissions.canMakeDecisions) return hidden();

    if (!isDecided(this.status)) return hidden();

    if (this.undoDecisionDays === null) return enabled;

    const TOOLTIP = `Undoing decisions is allowed for ${this.undoDecisionDays} days after a decision is made`;

    if (!this.decisionAt) return disabled(TOOLTIP);

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - this.undoDecisionDays);

    if (new Date(this.decisionAt) < cutoff) return disabled(TOOLTIP);

    return enabled;
  }

  get makeDecision(): ActionState {
    if (!this.permissions.canMakeDecisions) return hidden();

    if ([...DECIDED_STATUSES, ApplicationStatus.OnHold].includes(this.status))
      return hidden();

    if (
      [ApplicationStatus.Canceled, ApplicationStatus.Merged].includes(
        this.status
      )
    )
      return disabled(
        "You can make a decision once at least one application has been submitted."
      );

    if (this.requirements.length)
      return disabled(
        this.requirements.includes(
          MakeDecisionRequirements.HasPendingCountyReports
        )
          ? "You cannot make a decision while there are pending county walk-in requests."
          : "You can make a decision once at least one application has been submitted."
      );

    const hasSubmitted = this.applicants.some(
      (a) => a.application_status === ApplicantStatus.Submitted
    );

    if (!hasSubmitted)
      return disabled(
        "You can make a decision once at least one application has been submitted."
      );

    return enabled;
  }
}
